import { defer, merge, Observable, of } from "rxjs"
import { catchError, delayWhen, filter, switchMap } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { flatten } from "lodash"
import type { EpicDependencies } from "../rxjs/configureEpics"
import {
    FETCH_CUSTOMER_DETAIL_REQUESTED,
    FETCH_LOYALTY_PROGRAM_REQUESTED,
    fetchAccessAndRolesFailure,
    fetchAccessAndRolesSuccess,
    fetchAccountImagesSuccess,
    fetchCustomerDetailFailure,
    fetchCustomerDetailSuccess,
    fetchDepartmentsFailure,
    fetchDepartmentsSuccess,
    fetchInventoryItemsSuccess,
    fetchLoyaltyProgramFailure,
    fetchLoyaltyProgramSuccess,
    fetchRolesFailure,
    fetchRolesSuccess,
    fetchUserAccountsFailure,
    fetchUserAccountsSuccess,
} from "../../api/actions"
import { ACCOUNT_SELECTED, AGENDA_MOUNTED, COOKIE_LOADED, REFETCH_DATA } from "../auth/actions"
import { getHttpHeaders, getLocationsAuthData } from "../auth/selectors"
import {
    transformAccessAndRolesServerData,
    transformCustomerServerData,
    transformInventoryItemServerData,
    transformLoyaltyProgramData,
    transformUserAccountsServerData,
    transformVersions,
} from "./utils"
import {
    getAccesses,
    getLoyaltyProgramFormData,
    isEmployeesAgendaOpened,
    isInventoryAgendaOpened,
    isSchematizedAgendaOpened,
    isSummaryAgendaOpened,
} from "./selectors"
import { createLoadingEnded, createLoadingStarted } from "../filters/actions"
import { isRolesAgendaOpened } from "../screens/roles/selectors"
import { createActionItemsAndCategoriesWillMount } from "../screens/categories/actions"
import { SCHEMATIZED_AGENDA_MOUNTED } from "../screens/salesInput/schematized/actions"
import { EMPLOYEES_AGENDA_MOUNTED } from "../screens/employees/employees/actions"
import { INVENTORY_FETCH_REQUESTED, inventoryFetchRequested } from "../screens/inventory/actions"
import { waitForAccountSelected, waitForGlobalFilterSet } from "../screens/reports/customers/customersTable/epics"
import { createActionLoadingError } from "./actions"
import { Action, StateObject } from "../redux/types"

// Account Image

export const fetchAccountImagesRequestedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(actions$.pipe(ofType(ACCOUNT_SELECTED, COOKIE_LOADED)), actions$.pipe(ofType(REFETCH_DATA))).pipe(
        switchMap(() =>
            deps.api.getAccountImageApi(getHttpHeaders(deps.getState())).pipe(
                switchMap((response: any) => {
                    return [
                        fetchAccountImagesSuccess(
                            response.content
                                .filter(ai => !ai.deleted)
                                .map(ai => ({
                                    id: ai.id,
                                    locationId: ai.location_id,
                                    terminalId: ai.terminal_id,
                                    sortIndex: ai.sort_index,
                                    label: ai.label || null,
                                    path: ai.path,
                                    type: ai.type,
                                    awsBased: true,
                                })),
                            transformVersions(
                                response.content.map(obj => ({
                                    id: obj.id,
                                    version: obj.version,
                                }))
                            )
                        ),
                    ]
                }),
                catchError(error => of(createActionLoadingError(error)))
            )
        )
    )

export const fetchCustomerDetailEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CUSTOMER_DETAIL_REQUESTED),
        switchMap((action: Action) =>
            deps.api.getCustomerDetailApi(getHttpHeaders(deps.getState()), action.payload.customerId).pipe(
                switchMap(response => {
                    const loyaltyProgram = getLoyaltyProgramFormData(deps.getState())
                    return [
                        createLoadingEnded("customer-detail"),
                        createLoadingEnded("customer-orders"),
                        fetchCustomerDetailSuccess(
                            transformCustomerServerData(response, loyaltyProgram),
                            transformVersions([
                                ...response.content.map(obj => ({
                                    id: obj.id,
                                    version: obj.version,
                                })),
                                ...flatten(
                                    response.content.map(
                                        (
                                            obj // We need to save also versions of addresses
                                        ) =>
                                            obj.addresses.map((a: any) => ({
                                                id: a.id,
                                                version: a.version,
                                            }))
                                    )
                                ),
                                ...flatten(
                                    response.content.map(
                                        (
                                            obj // We need to save also versions of loyalty
                                        ) =>
                                            obj.customer_loyalty_programs.map(a => ({
                                                id: a.id,
                                                version: a.version,
                                            }))
                                    )
                                ),
                            ])
                        ),
                    ]
                }),
                catchError(error => of(fetchCustomerDetailFailure(error)))
            )
        )
    )

// Roles
export const fetchingRolesRequested = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(ACCOUNT_SELECTED)),
        actions$.pipe(ofType(REFETCH_DATA)),
        actions$.pipe(ofType(AGENDA_MOUNTED)),
        actions$.pipe(ofType(EMPLOYEES_AGENDA_MOUNTED))
    ).pipe(
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() =>
            deps.api.getRoleApi(getHttpHeaders(deps.getState())).pipe(
                switchMap(response => {
                    return [
                        fetchRolesSuccess(
                            response,
                            transformVersions(
                                response.map(obj => ({
                                    id: obj.id,
                                    version: obj.version,
                                }))
                            )
                        ),
                    ]
                }),
                catchError(error => of(fetchRolesFailure(error)))
            )
        )
    )
// Departments

export const fetchingDepartmentsRequested = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(actions$.pipe(ofType(ACCOUNT_SELECTED, COOKIE_LOADED)), actions$.pipe(ofType(REFETCH_DATA))).pipe(
        switchMap(() =>
            deps.api.getDepartmentApi(getHttpHeaders(deps.getState())).pipe(
                switchMap(response => {
                    return [
                        fetchDepartmentsSuccess(
                            response,
                            transformVersions(
                                response.map(obj => ({
                                    id: obj.id,
                                    version: obj.version,
                                }))
                            )
                        ),
                    ]
                }),
                catchError(error => of(fetchDepartmentsFailure(error)))
            )
        )
    )
// Inventory

export const inventoryLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED, REFETCH_DATA, AGENDA_MOUNTED),
        delayWhen(action => waitForAccountSelected(actions$, deps.getState(), action)),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        filter(() => isInventoryAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("inventory"), inventoryFetchRequested()])
    )

export const fetchInventoryRequested = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(INVENTORY_FETCH_REQUESTED),
        switchMap(() =>
            deps.api.getInventoryApi(getHttpHeaders(deps.getState())).pipe(
                switchMap(response => {
                    const locations = getLocationsAuthData(deps.getState())
                    return [
                        createLoadingEnded("inventory"),
                        fetchInventoryItemsSuccess(
                            transformInventoryItemServerData(response, locations),
                            transformVersions(
                                flatten([
                                    ...response.content.map(obj => ({
                                        id: obj.id,
                                        version: obj.version,
                                    })),
                                    ...flatten(
                                        response.content.map(l =>
                                            l.inventory_item_locations.map(loc => ({
                                                id: loc.id,
                                                version: loc.version,
                                            }))
                                        )
                                    ),
                                ])
                            )
                        ),
                    ]
                }),
                catchError(error => of(createActionLoadingError(error)))
            )
        )
    )
// Items and categories

export const categoriesLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED, REFETCH_DATA, AGENDA_MOUNTED, SCHEMATIZED_AGENDA_MOUNTED), // TODO remove after root category refactor
        filter(() => isSchematizedAgendaOpened(deps.getState())),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() => [createLoadingStarted("itemsAndCategories"), createActionItemsAndCategoriesWillMount()])
    )

export const fetchLoyaltyProgramRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_LOYALTY_PROGRAM_REQUESTED),
        switchMap(() =>
            deps.api.getLoyaltyProgramApi(getHttpHeaders(deps.getState())).pipe(
                switchMap(response => [
                    createLoadingEnded("loyalty-program"),
                    fetchLoyaltyProgramSuccess(
                        transformLoyaltyProgramData(response.content),
                        transformVersions(
                            response.content.map(grid => ({
                                id: grid.id,
                                version: grid.version,
                            }))
                        )
                    ),
                ]),
                catchError(error => of(fetchLoyaltyProgramFailure(error)))
            )
        )
    )
// User Accounts

export const userAccountsDataLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(ACCOUNT_SELECTED, COOKIE_LOADED)),
        actions$.pipe(
            ofType(REFETCH_DATA),
            filter(() => !!(isEmployeesAgendaOpened(deps.getState()) || isRolesAgendaOpened(deps.getState())))
        )
    ).pipe(switchMap(() => [createLoadingStarted("employees")]))

export const fetchUserAccountsDataRequested = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(ACCOUNT_SELECTED, COOKIE_LOADED)),
        actions$.pipe(
            ofType(REFETCH_DATA),
            filter(() => !!(isEmployeesAgendaOpened(deps.getState()) || isRolesAgendaOpened(deps.getState())))
        )
    ).pipe(
        switchMap(() =>
            deps.api.getUserAccountApi(getHttpHeaders(deps.getState()), {}).pipe(
                switchMap(response => {
                    return [
                        createLoadingEnded("employees"),
                        fetchUserAccountsSuccess(
                            transformUserAccountsServerData(response),
                            transformVersions(
                                flatten(
                                    response.content.map(obj => {
                                        return [
                                            {
                                                id: obj.id,
                                                version: obj.version,
                                            },
                                            {
                                                id: obj.user_id,
                                                version: parseInt(obj.user_version, 10),
                                            },
                                            ...(obj.locations
                                                ? obj.locations.map(l => ({
                                                      id: l.id,
                                                      version: l.version,
                                                  }))
                                                : []),
                                            ...(obj.roles
                                                ? obj.roles.map(l => ({
                                                      id: l.id,
                                                      version: l.version,
                                                  }))
                                                : []),
                                            ...(obj.addresses
                                                ? obj.addresses.map(l => ({
                                                      id: l.id,
                                                      version: l.version,
                                                  }))
                                                : []),
                                            ...(obj.notes
                                                ? obj.notes.map(l => ({
                                                      id: l.id,
                                                      version: l.version,
                                                  }))
                                                : []),
                                        ]
                                    })
                                )
                            )
                        ),
                    ]
                }),
                catchError(error => of(fetchUserAccountsFailure(error)))
            )
        )
    )
// Access and roles loading

export const fetchAccessAndRolesDataLoading = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED, REFETCH_DATA, AGENDA_MOUNTED, EMPLOYEES_AGENDA_MOUNTED),
        filter(
            () =>
                isEmployeesAgendaOpened(deps.getState()) ||
                isRolesAgendaOpened(deps.getState()) ||
                isSummaryAgendaOpened(deps.getState())
        ),
        switchMap(() => [createLoadingStarted("roles")])
    )
// Access and roles

export const fetchAccessAndRolesDataRequested = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED, REFETCH_DATA, AGENDA_MOUNTED, EMPLOYEES_AGENDA_MOUNTED),
        filter(
            () =>
                isEmployeesAgendaOpened(deps.getState()) ||
                isRolesAgendaOpened(deps.getState()) ||
                isSummaryAgendaOpened(deps.getState()) ||
                !getAccesses(deps.getState()).length
        ),
        delayWhen(action => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() =>
            defer(() =>
                deps.api.getAccessAndRolesApi(getHttpHeaders(deps.getState())).pipe(
                    switchMap(data => [
                        createLoadingEnded("roles"),
                        fetchAccessAndRolesSuccess(
                            transformAccessAndRolesServerData(data),
                            transformVersions(
                                flatten(
                                    data.map(obj => {
                                        return [
                                            {
                                                id: obj.id,
                                                version: obj.version,
                                            },
                                            ...obj.permissions.map(p => ({
                                                id: p.id,
                                                version: p.version,
                                            })),
                                        ]
                                    })
                                )
                            )
                        ),
                    ]),
                    catchError(error => of(fetchAccessAndRolesFailure(error)))
                )
            )
        )
    )

export default [
    fetchAccessAndRolesDataLoading,
    fetchAccountImagesRequestedEpic,
    fetchInventoryRequested,
    inventoryLoadingStartedEpics,
    fetchAccessAndRolesDataRequested,
    fetchingRolesRequested,
    fetchingDepartmentsRequested,
    fetchUserAccountsDataRequested,
    userAccountsDataLoadingStartedEpics,
    fetchCustomerDetailEpic,
    categoriesLoadingStartedEpics,
    fetchLoyaltyProgramRequestedEpics,
]
